import type {
  PlatformGuestOrderCta,
  PlatformLocation,
  PlatformOrderingConfig,
} from "@/lib/restaurant-platform";

/**
 * Check if the order feature is enabled
 * Set NEXT_PUBLIC_DISABLE_ORDER=true to disable the order feature
 */
export function isOrderFeatureEnabled(): boolean {
  return process.env.NEXT_PUBLIC_DISABLE_ORDER !== "true";
}

/**
 * When false (default), the storefront is pickup-only: no delivery toggle, no address gate.
 * Set NEXT_PUBLIC_ORDER_DELIVERY_ENABLED=true when delivery is fully wired (zones, fees, address).
 */
export function isDeliveryOrderingEnabled(): boolean {
  return process.env.NEXT_PUBLIC_ORDER_DELIVERY_ENABLED === "true";
}

export type OrderingFeatureConfig = PlatformOrderingConfig;

export function getOrderingFeatureConfig(
  location: PlatformLocation,
): OrderingFeatureConfig {
  return location.ordering;
}

export function isFirstPartyOrderingEnabledForLocation(
  location: PlatformLocation,
): boolean {
  if (!isOrderFeatureEnabled()) {
    return false;
  }

  return (
    location.ordering.mode === "first_party" &&
    location.ordering.pickupEnabled
  );
}

/**
 * Guest "Order" destination from location ordering mode, never from
 * `isDefaultLocation`. First-party tenants use their configured `/order`
 * path (or `/order` when that field is empty). External tenants use
 * `orderUrl` (Grubhub, etc.) and fail closed to an empty href when missing
 * so unknown/incomplete configs do not fall back to Tuscan checkout.
 */
export function resolvePublicOrderHref(input: {
  orderingMode: PlatformOrderingConfig["mode"];
  orderUrl: string | undefined;
}): string {
  const configured = input.orderUrl?.trim() ?? "";

  if (input.orderingMode === "first_party") {
    return configured || "/order";
  }

  return configured;
}

/**
 * Public Order CTA href. Uses `guestOrderCta` when set so we can park guest
 * buttons on Grubhub without changing `ordering.mode` or first-party `/order`.
 */
export function resolveGuestFacingOrderHref(input: {
  orderingMode: PlatformOrderingConfig["mode"];
  orderUrl: string | undefined;
  guestOrderCta?: PlatformGuestOrderCta;
}): string {
  const override = input.guestOrderCta?.href.trim() ?? "";
  if (override) {
    return override;
  }

  return resolvePublicOrderHref(input);
}

export function resolveGuestFacingOrderCtaLabel(
  guestOrderCta: PlatformGuestOrderCta | undefined,
  fallback: string,
): string {
  if (guestOrderCta?.provider === "grubhub") {
    return "Order on Grubhub";
  }

  return fallback;
}

/**
 * Where an empty first-party checkout should send the guest to add items.
 * `/order` is the ordering catalog for first-party locations (Tuscan and
 * Roma SH). `/menu` is the public browse menu and, on Roma, a chooser host —
 * not the cart they just left.
 */
export function resolveEmptyCheckoutReturnHref(input: {
  orderingMode: PlatformOrderingConfig["mode"];
  orderUrl: string | undefined;
}): string {
  if (input.orderingMode === "first_party") {
    return resolvePublicOrderHref(input);
  }

  return "/menu";
}

export function isMenuCartBrowsingEnabledForLocation(
  location: PlatformLocation,
): boolean {
  return (
    location.ordering.mode === "first_party" &&
    location.ordering.pickupEnabled
  );
}

export function isDeliveryOrderingEnabledForLocation(
  location: PlatformLocation,
): boolean {
  if (!isFirstPartyOrderingEnabledForLocation(location)) {
    return false;
  }

  return location.ordering.deliveryEnabled;
}
